import React, { useState } from 'react';
import Tooltip from 'rc-tooltip';
import classNames from 'classnames';
import { faFlag } from '@fortawesome/free-solid-svg-icons/faFlag';
import { useForm } from 'react-hook-form';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';

import { useFlagItemMutation, FlagItemMutationVariables } from 'interfaces/graphql-typings';

import styles from './flagCommentStyles.scss';

const FlagComment: React.FC<{ commentId: string }> = ({ commentId }): React.ReactElement => {
  const [flagged, setFlagged] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<FlagItemMutationVariables>({
    defaultValues: {
      itemId: commentId,
      category: 'content',
      description: '',
      type: 'Comment'
    }
  });

  const [flagItem, { error, loading }] = useFlagItemMutation({
    onCompleted: () => setFlagged(true)
  });

  if (error) throw error;

  const renderConfirmation = (): React.ReactElement | string => {
    return showConfirmation ? (
      <form
        onSubmit={e => {
          e.stopPropagation();
          handleSubmit(formInput =>
            flagItem({
              variables: {
                ...formInput,
                description: formInput.description || 'Flag as inappropriate/inaccurate'
              }
            })
          )(e);
        }}
      >
        <p>Are you sure you want to report this comment?</p>
        <div>
          <textarea
            className={styles.reasonInput}
            {...register('description', { required: false })}
            placeholder="Reasons for flagging (optional)"
          />
          <Button
            isLoading={loading}
            text="Report"
            size={BUTTON_SIZES.TINY}
            type={BUTTON_TYPES.DANGER}
            width={BUTTON_WIDTHS.FULL}
          />
          <Button
            disabled={loading || isSubmitting}
            text="Cancel"
            size={BUTTON_SIZES.TINY}
            onClick={() => setShowConfirmation(false)}
            width={BUTTON_WIDTHS.FULL}
          />
        </div>
      </form>
    ) : (
      'Flag as inappropriate/inaccurate'
    );
  };

  return (
    <Tooltip
      overlay={flagged ? 'Comment has been reported' : renderConfirmation}
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.5}
      placement="top"
    >
      <div className={classNames(styles.flagComment, { [styles.flagged]: flagged })}>
        <Button
          isLoading={loading}
          type={BUTTON_TYPES.LINK}
          faIcon={faFlag}
          onClick={() => setShowConfirmation(true)}
          disabled={flagged}
          text={flagged ? 'Reported' : 'Report'}
          size={BUTTON_SIZES.SMALL}
        />
      </div>
    </Tooltip>
  );
};

export default FlagComment;
